/**
 * Конструктор чек-листа (КЧ-04, КЧ-14): шаблон компании с пунктами трёх видов — «соответствует / не соответствует /
 * не применимо», число с допуском, дата. Правится без программиста; приёмки, начатые раньше, хранят свою копию.
 */
import {
  CHECKLIST_ANSWER_TYPES,
  CHECKLIST_REMARK_KINDS,
  CHECKLIST_REMARK_SOURCES,
  CHECKLIST_WORK_KINDS,
  type ChecklistAnswerType,
  type ChecklistRemarkKind,
  type ChecklistRemarkSource,
  type ChecklistTemplateDetail,
  type ChecklistWorkKind,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { useCompanyWide } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';

interface ItemForm {
  text: string;
  answerType: ChecklistAnswerType;
  unit: string;
  minValue: string;
  maxValue: string;
  normRef: string;
}
const emptyItem = (): ItemForm => ({
  text: '',
  answerType: 'conformity',
  unit: '',
  minValue: '',
  maxValue: '',
  normRef: '',
});

const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));

export function TemplatePage() {
  const { templateId } = useParams({ strict: false }) as { templateId: string };
  const isNew = templateId === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canEdit = useCompanyWide('qhse.edit');
  const q = useQuery({
    queryKey: ['qa-template', templateId],
    queryFn: () => api<ChecklistTemplateDetail>(`/quality/checklist-templates/${templateId}`),
    enabled: !isNew,
  });
  useCrumbs(isNew ? [{ label: 'Новый шаблон' }] : q.data ? [{ label: q.data.title }] : null, q.error);
  const [f, setF] = useState({
    title: '',
    workKind: 'reinforcement' as ChecklistWorkKind,
    description: '',
    remarkSource: 'otk' as ChecklistRemarkSource,
    remarkKind: 'remark' as ChecklistRemarkKind,
    remarkDays: '7',
  });
  const [items, setItems] = useState<ItemForm[]>([emptyItem()]);
  useEffect(() => {
    const t = q.data;
    if (!t) return;
    setF({
      title: t.title,
      workKind: t.workKind,
      description: t.description ?? '',
      remarkSource: t.remarkSource,
      remarkKind: t.remarkKind,
      remarkDays: String(t.remarkDays),
    });
    setItems(
      t.items.map((i) => ({
        text: i.text,
        answerType: i.answerType,
        unit: i.unit ?? '',
        minValue: i.minValue == null ? '' : String(i.minValue),
        maxValue: i.maxValue == null ? '' : String(i.maxValue),
        normRef: i.normRef ?? '',
      })),
    );
  }, [q.data]);
  const body = () => ({
    title: f.title.trim(),
    workKind: f.workKind,
    description: f.description.trim() || null,
    remarkSource: f.remarkSource,
    remarkKind: f.remarkKind,
    remarkDays: Number(f.remarkDays) || 7,
    items: items.map((i) => ({
      text: i.text.trim(),
      answerType: i.answerType,
      unit: i.answerType === 'number' ? i.unit.trim() || null : null,
      minValue: i.answerType === 'number' ? num(i.minValue) : null,
      maxValue: i.answerType === 'number' ? num(i.maxValue) : null,
      normRef: i.normRef.trim() || null,
    })),
  });
  const save = useMutation({
    mutationFn: () =>
      isNew
        ? api<ChecklistTemplateDetail>('/quality/checklist-templates', { body: body() })
        : api<ChecklistTemplateDetail>(`/quality/checklist-templates/${templateId}`, {
            method: 'PUT',
            body: body(),
          }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['qa-templates'] });
      qc.setQueryData(['qa-template', t.id], t);
      toast(isNew ? 'Шаблон создан' : `Шаблон сохранён, версия ${t.revision}`);
      if (isNew) navigate({ to: '/qhse/templates/$templateId', params: { templateId: t.id }, replace: true });
    },
  });
  const archive = useMutation({
    mutationFn: (status: 'active' | 'archived') =>
      api<ChecklistTemplateDetail>(`/quality/checklist-templates/${templateId}/status`, {
        method: 'POST',
        body: { status },
      }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['qa-templates'] });
      qc.setQueryData(['qa-template', t.id], t);
      toast(t.status === 'archived' ? 'Шаблон убран в архив' : 'Шаблон возвращён');
    },
  });
  const setItem = (k: number, patch: Partial<ItemForm>) =>
    setItems((xs) => xs.map((x, i) => (i === k ? { ...x, ...patch } : x)));
  const move = (k: number, d: number) =>
    setItems((xs) => {
      const j = k + d;
      if (j < 0 || j >= xs.length) return xs;
      const next = [...xs];
      [next[k], next[j]] = [next[j]!, next[k]!];
      return next;
    });
  if (!isNew && q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (!isNew && q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const ready = f.title.trim().length > 0 && items.length > 0 && items.every((i) => i.text.trim().length > 0);
  return (
    <Page>
      <PageHeader
        title={isNew ? 'Новый шаблон чек-листа' : f.title || 'Шаблон чек-листа'}
        subtitle={
          q.data ? (
            <span className="flex flex-wrap items-center gap-2">
              <span>Версия {q.data.revision}</span>
              {q.data.status === 'archived' ? <Badge tone="warning">В архиве</Badge> : null}
            </span>
          ) : undefined
        }
        actions={
          canEdit ? (
            <>
              {!isNew && q.data ? (
                <Button
                  variant="secondary"
                  size="lg"
                  loading={archive.isPending}
                  onClick={() => archive.mutate(q.data!.status === 'archived' ? 'active' : 'archived')}
                >
                  {q.data.status === 'archived' ? 'Вернуть из архива' : 'В архив'}
                </Button>
              ) : null}
              <Button
                variant="primary"
                size="lg"
                loading={save.isPending}
                disabled={!ready}
                onClick={() => save.mutate()}
              >
                Сохранить
              </Button>
            </>
          ) : null
        }
      />
      {!canEdit ? (
        <p className="mb-4 text-[14px] text-ink-2">
          Шаблоны правит тот, кто ведёт проверки по всей компании. Вы можете только смотреть.
        </p>
      ) : null}
      {save.isError ? <ErrorBox error={save.error} className="mb-4" /> : null}
      <fieldset disabled={!canEdit} className="flex min-w-0 flex-col gap-6">
        <Panel title="Описание">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Название" className="sm:col-span-2">
              <Input
                className="h-11 md:h-10"
                value={f.title}
                onChange={(e) => setF({ ...f, title: e.target.value })}
                placeholder="Приёмка арматурного каркаса"
              />
            </Field>
            <Field label="Вид работ">
              <Select
                className="h-11 md:h-10"
                value={f.workKind}
                onChange={(e) => setF({ ...f, workKind: e.target.value as ChecklistWorkKind })}
              >
                {(Object.keys(CHECKLIST_WORK_KINDS) as ChecklistWorkKind[]).map((k) => (
                  <option key={k} value={k}>
                    {CHECKLIST_WORK_KINDS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Срок устранения замечания, дней" hint="От дня, когда ответили «не соответствует»">
              <Input
                className="h-11 md:h-10"
                inputMode="numeric"
                value={f.remarkDays}
                onChange={(e) => setF({ ...f, remarkDays: e.target.value })}
              />
            </Field>
            <Field label="Замечание в реестре: источник">
              <Select
                className="h-11 md:h-10"
                value={f.remarkSource}
                onChange={(e) => setF({ ...f, remarkSource: e.target.value as ChecklistRemarkSource })}
              >
                {(Object.keys(CHECKLIST_REMARK_SOURCES) as ChecklistRemarkSource[]).map((k) => (
                  <option key={k} value={k}>
                    {CHECKLIST_REMARK_SOURCES[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Замечание в реестре: вид">
              <Select
                className="h-11 md:h-10"
                value={f.remarkKind}
                onChange={(e) => setF({ ...f, remarkKind: e.target.value as ChecklistRemarkKind })}
              >
                {(Object.keys(CHECKLIST_REMARK_KINDS) as ChecklistRemarkKind[]).map((k) => (
                  <option key={k} value={k}>
                    {CHECKLIST_REMARK_KINDS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Пояснение" className="sm:col-span-2">
              <Textarea
                rows={2}
                value={f.description}
                onChange={(e) => setF({ ...f, description: e.target.value })}
              />
            </Field>
          </div>
        </Panel>
        <Panel
          title="Пункты"
          meta={`${items.length}`}
          actions={
            canEdit ? (
              <Button
                icon={<Plus className="size-4" />}
                onClick={() => setItems((xs) => [...xs, emptyItem()])}
              >
                Добавить пункт
              </Button>
            ) : null
          }
        >
          <ol className="flex flex-col gap-4">
            {items.map((it, k) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: пункты без постоянного номера, порядок меняет сам человек
              <li key={k} className="rounded-md border border-line p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-[13px] font-medium text-ink-2">Пункт {k + 1}</span>
                  {canEdit ? (
                    <span className="flex items-center">
                      <IconButton label="Выше" disabled={k === 0} onClick={() => move(k, -1)}>
                        <ArrowUp className="size-4" />
                      </IconButton>
                      <IconButton label="Ниже" disabled={k === items.length - 1} onClick={() => move(k, 1)}>
                        <ArrowDown className="size-4" />
                      </IconButton>
                      <IconButton
                        label="Убрать пункт"
                        disabled={items.length === 1}
                        onClick={() => setItems((xs) => xs.filter((_, i) => i !== k))}
                      >
                        <Trash2 className="size-4" />
                      </IconButton>
                    </span>
                  ) : null}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Что проверить" className="sm:col-span-2">
                    <Input
                      className="h-11 md:h-10"
                      value={it.text}
                      onChange={(e) => setItem(k, { text: e.target.value })}
                    />
                  </Field>
                  <Field label="Вид ответа">
                    <Select
                      className="h-11 md:h-10"
                      value={it.answerType}
                      onChange={(e) => setItem(k, { answerType: e.target.value as ChecklistAnswerType })}
                    >
                      {(Object.keys(CHECKLIST_ANSWER_TYPES) as ChecklistAnswerType[]).map((t) => (
                        <option key={t} value={t}>
                          {CHECKLIST_ANSWER_TYPES[t]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Норма" hint="СП РК, СН РК, ГОСТ, пункт">
                    <Input
                      className="h-11 md:h-10"
                      value={it.normRef}
                      onChange={(e) => setItem(k, { normRef: e.target.value })}
                    />
                  </Field>
                  {it.answerType === 'number' ? (
                    <>
                      <Field label="Допуск от">
                        <Input
                          className="h-11 md:h-10"
                          inputMode="decimal"
                          value={it.minValue}
                          onChange={(e) => setItem(k, { minValue: e.target.value })}
                        />
                      </Field>
                      <Field label="Допуск до">
                        <Input
                          className="h-11 md:h-10"
                          inputMode="decimal"
                          value={it.maxValue}
                          onChange={(e) => setItem(k, { maxValue: e.target.value })}
                        />
                      </Field>
                      <Field label="Единица">
                        <Input
                          className="h-11 md:h-10"
                          value={it.unit}
                          onChange={(e) => setItem(k, { unit: e.target.value })}
                          placeholder="мм"
                        />
                      </Field>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        </Panel>
      </fieldset>
    </Page>
  );
}
